"use client";

import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
import { useState, useMemo, useEffect, useRef } from "react";

import useLichThiDau from "@/hooks/useLichThiDau";
import useHotLeagues from "@/hooks/useHotLeagues";
import MatchRound from "@/components/MatchRound";
import LoadingInner from "@/components/Loading";
import NoData from "@/components/NoData";
import HotLeagueTabs from "@/components/HotLeagueTabs";
import useLeagueBySlug from "@/hooks/useLeagueBySlug";
import LeagueSearchDropdown from "@/components/LeagueSearchDropdown";
import useAllLeagues from "@/hooks/useAllLeagues";

dayjs.extend(utc);
dayjs.extend(timezone);

const INITIAL_ROUND_COUNT = 2;

const LichThiDau = () => {
  const [activeLeague, setActiveLeague] = useState(null);
  const [visibleRoundCount, setVisibleRoundCount] =
    useState(INITIAL_ROUND_COUNT);

  const loadMoreRef = useRef(null);

  const { leagues } = useHotLeagues({ limit: 10 });
  const { rounds, loading } = useLichThiDau(activeLeague?.id);

  const { league } = useLeagueBySlug(activeLeague?.slug);
  const { leagues: allLeagues } = useAllLeagues();

  const isCup = !!league?.isCup;


  const sortedRounds = useMemo(() => {
    if (!rounds?.length) return [];

    if (!isCup) {
      return [...rounds].sort((a, b) => parseInt(a.round) - parseInt(b.round));
    }

    return [...rounds].sort((a, b) => a.matchTime - b.matchTime);
  }, [rounds, isCup]);

  const validRounds = useMemo(() => {
    if (!sortedRounds.length) return [];

    const currentRound = league?.currentRound;

    if (currentRound) {
      if (!isCup) {
        const startIndex = sortedRounds.findIndex(
          (r) => parseInt(r.round) === parseInt(currentRound)
        );

        if (startIndex !== -1) {
          return sortedRounds.slice(startIndex);
        }
      }

      if (isCup) {
        const startIndex = sortedRounds.findIndex(
          (r) => r.round?.toLowerCase() === currentRound.toLowerCase()
        );

        if (startIndex !== -1) {
          return sortedRounds.slice(startIndex);
        }
      }
    }

    return sortedRounds;
  }, [sortedRounds, league?.currentRound, isCup]);

  useEffect(() => {
    setVisibleRoundCount(INITIAL_ROUND_COUNT);
  }, [activeLeague?.id]);

  const visibleRounds = useMemo(() => {
    return validRounds.slice(0, visibleRoundCount);
  }, [validRounds, visibleRoundCount]);

  const canLoadMoreRef = useRef(true);

  useEffect(() => {
    const el = loadMoreRef.current;
    if (!el) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (!entry.isIntersecting) return;
        if (!canLoadMoreRef.current) return;

        canLoadMoreRef.current = false;

        setVisibleRoundCount((prev) => Math.min(prev + 1, validRounds.length));
      },
      {
        rootMargin: "300px",
      }
    );

    observer.observe(el);
    return () => observer.disconnect();
  }, [validRounds.length, activeLeague?.id]);

  useEffect(() => {
    canLoadMoreRef.current = true;
  }, [visibleRoundCount]);

  useEffect(() => {
    setVisibleRoundCount(INITIAL_ROUND_COUNT);
    canLoadMoreRef.current = true;
  }, [activeLeague?.id]);

  return (
    <div className="flex flex-col gap-2">
      <HotLeagueTabs
        leagues={leagues}
        activeLeagueId={activeLeague?.id}
        onChange={(league) => setActiveLeague(league)}
      />

      <LeagueSearchDropdown
        leagues={allLeagues}
        onSelect={(league) => setActiveLeague(league)}
      />

      {loading && <LoadingInner />}

      {!loading && (
        <div className="space-y-2 rounded-lg p-2">
          {visibleRounds.length === 0 ? (
            <NoData message="Không có trận đấu nào" />
          ) : (
            <>
              {visibleRounds.map((item) => (
                <MatchRound
                  key={item.round}
                  leagueId={activeLeague?.id}
                  round={item.round}
                  isCup={isCup}
                  currentRound={league?.currentRound}
                />
              ))}

              {visibleRoundCount < validRounds.length && (
                <div ref={loadMoreRef} className="h-20"></div>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
};

export default LichThiDau;
